import { SALUTATIONS } from '../../../../lib/salutations';
import { uid } from '../../../../lib/uid';
import type { SupplierRequest } from '../supplierTypes';

/**
 * A synthetic supplier, for filling the form in one click.
 *
 * **THE CONTACT'S NAME IS OBVIOUSLY SYNTHETIC, and that is not a style choice.** A generated
 * supplier writes a row into `rsys_pii` — the one store whose whole purpose is holding real
 * people's identities — and a fabricated-but-realistic name sitting there is indistinguishable
 * from a real one to anyone auditing it later. "Is this row a person?" is the question that store
 * exists to make answerable, so the generator answers it: `Placeholder Testcase`, the shape
 * `samplePersonnel` and `sampleReference` already take.
 *
 * **The COMPANY name is plausible vocabulary**, because it never leaves `rsys_`. The test is
 * where a value LANDS, not which generator it came from — the same split `sampleEducation`
 * takes with a school's name.
 *
 * **The number is from the reserved `+63 917 555` block** and the email domain is
 * `example.test`, which can never resolve.
 */
export function sampleSupplier(): SupplierRequest {
  const suffix = uid().slice(0, 6);

  const trade = pick(TRADE_NAMES);
  const kind = pick(COMPANY_KINDS);
  const place = pick(PLACES);

  return {
    // SUFFIXED, because the name is unique among live rows: without it the second click
    // produces a name the server rejects as already registered.
    name: `${trade} ${kind} ${suffix.toUpperCase()}`,
    email: `orders.${suffix}@example.test`,
    phone: `+63 917 555 ${suffix.replace(/\D/g, '0').slice(0, 4).padEnd(4, '0')}`,

    // A plausible SHAPE rather than a real TIN — twelve digits with the dashes PH prints.
    taxIdentificationNumber: `${digits(3)}-${digits(3)}-${digits(3)}-000`,

    province: place.province,
    cityMunicipality: place.city,
    barangay: null,
    streetAddress: `${1 + Math.floor(Math.random() * 400)} ${pick(STREETS)}`,
    notes: pick(NOTES),

    /*
      OBVIOUSLY SYNTHETIC. See the remarks above — this pair is written into `rsys_pii`, and it
      is the one field on this form where a realistic value would be the wrong answer.
    */
    contactSalutation: SALUTATIONS[Math.floor(Math.random() * SALUTATIONS.length)],
    contactFirstName: 'Placeholder',
    contactMiddleName: null,
    contactLastName: 'Testcase',
    contactSuffix: null,
  };
}

function pick<T>(pool: readonly T[]): T {
  return pool[Math.floor(Math.random() * pool.length)];
}

function digits(count: number): string {
  return Array.from({ length: count }, () => Math.floor(Math.random() * 10)).join('');
}

const TRADE_NAMES = [
  'Northgate',
  'Silverline',
  'Bayanihan',
  'Everbright',
  'Kanlaon',
  'Pacific Crest',
  'Maharlika',
] as const;

const COMPANY_KINDS = [
  'Trading',
  'Supplies Inc.',
  'Distribution Corp.',
  'Enterprises',
  'Industrial Sales',
] as const;

const PLACES = [
  { province: 'Rizal', city: 'Antipolo' },
  { province: 'Metro Manila', city: 'Quezon City' },
  { province: 'Metro Manila', city: 'Makati' },
  { province: 'Cavite', city: 'Bacoor' },
  { province: 'Laguna', city: 'Santa Rosa' },
] as const;

const STREETS = [
  'Sumulong Highway',
  'Ortigas Avenue Extension',
  'Marcos Highway',
  'Aguinaldo Highway',
  'Governor Drive',
] as const;

const NOTES = [
  'Delivers within Metro Manila and Rizal. Confirm stock before raising an order.',
  'Quotations are valid for thirty days. Ask for the current price list before ordering.',
  'Accepts partial deliveries. Invoices are issued per delivery rather than per order.',
  null,
] as const;
